import { useState, useEffect, useMemo, useCallback, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@cp/src/store';
import { IOption } from '@cp/src/components/AutocompleteDropdown/interfaces';
import EmbeddedPlayer from './EmbeddedPlayer';
import DetailsPopper from './DetailsPopper';
import Footer from './Footer';
import PreAuditBanner from './PreAudit/PreAuditBanner';
import SingleSelectQuestions from './Questions/SingleSelectQuestions';
import ViolationQuestion from './Questions/ViolationQuestion';
import PreAuditValuesSection from './PreAudit/PreAuditValuesSection';
import MultiSelectErrorSection from './Questions/MultiSelectErrorSection';
import ErrorRemovalReasonSection from './Questions/ErrorRemovalReasonSection';
import {
  ApiAuditTrailAnswer,
  AuditAnswer,
  AuditFeedbackFormProps,
  AuditQuestionConfig,
  CallingAgentAuditDetails,
  RemovedError
} from '../interface';
import { submitQcTeamLeadAudit, submitQcAgentAudit } from '../action';
import { getDropdownOptions } from '../utils';
import { Roles } from '../../auth/constants/AuthConstants';
import { AuditStatus } from './types';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import { AuditorType, QuestionTag, QuestionType, OptionTag } from './auditEnums';

const AuditFeedbackForm: FC<AuditFeedbackFormProps> = ({
  onSubmit,
  readOnly = false,
  isDrawerOpen = true
}) => {
  const dispatch = useDispatch();

  const auditAssignmentReferenceId =
    useSelector((state: RootState) => state.callAudit?.selectedAuditAssignmentReferenceId) || '';
  const tlReferenceId =
    useSelector((state: RootState) => state.common?.userData?.referenceId) || '';
  const userRoles = useSelector((state: RootState) => state?.common?.userData?.roles);
  const auditDetails = useSelector(
    (state: RootState) => state.callAudit?.callingAgentAuditDetails
  ) as CallingAgentAuditDetails | null;
  const isSidebarLoading = useSelector((state: RootState) => state.callAudit?.isSidebarLoading);

  const isQcAgent = userRoles?.includes(Roles.ROLE_PX_AUDIT_AGENT);
  const isTeamLead = userRoles?.includes(Roles.ROLE_PX_AUDIT_TEAM_LEAD);

  const auditQuestionnaire = (auditDetails?.auditConfig?.auditQuestionnaire ||
    []) as AuditQuestionConfig[];
  const auditTrail = auditDetails?.auditTrail || [];
  const recordingUrl = auditDetails?.recordingUrl || '';
  const auditStatus = auditDetails?.auditStatus || '';

  const dsAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.DS),
    [auditTrail]
  );

  const formPrefilledAnswers = useMemo(() => {
    const qcTeamLeadAuditTrail = auditTrail.find(t => t.auditor === AuditorType.QC_TEAM_LEAD);
    const qcAgentAuditTrail = auditTrail.find(t => t.auditor === AuditorType.QC_AGENT);
    if (qcTeamLeadAuditTrail) {
      return (qcTeamLeadAuditTrail.answers || []) as unknown as ApiAuditTrailAnswer[];
    }
    if (qcAgentAuditTrail) {
      return (qcAgentAuditTrail.answers || []) as unknown as ApiAuditTrailAnswer[];
    }
    return (dsAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];
  }, [auditTrail, dsAuditTrail]);

  const singleSelectQuestions = useMemo(
    () =>
      auditQuestionnaire.filter(
        q =>
          q.question_type === QuestionType.SINGLE_SELECT &&
          q.question_tag !== QuestionTag.IS_VIOLATION
      ),
    [auditQuestionnaire]
  );

  const violationQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.IS_VIOLATION),
    [auditQuestionnaire]
  );

  const violationReasonQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.VIOLATION_REASON_LIST),
    [auditQuestionnaire]
  );

  const clearanceReasonQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.CLEARANCE_REASON_LIST),
    [auditQuestionnaire]
  );

  const [answers, setAnswers] = useState<Record<number, number[]>>({});
  const [errorRemovalReasons, setErrorRemovalReasons] = useState<Record<number, IOption[]>>({});

  const initialMultiSelectIds = useMemo(() => {
    const initials: Record<number, number[]> = {};
    if (violationReasonQuestion) {
      const answer = formPrefilledAnswers.find(
        (a: ApiAuditTrailAnswer) => a.question_id === violationReasonQuestion.question_id
      );
      if (answer) {
        initials[violationReasonQuestion.question_id] = answer.selected_option_ids;
      }
    }
    if (clearanceReasonQuestion) {
      const answer = formPrefilledAnswers.find(
        (a: ApiAuditTrailAnswer) => a.question_id === clearanceReasonQuestion.question_id
      );
      if (answer) {
        initials[clearanceReasonQuestion.question_id] = answer.selected_option_ids;
      }
    }
    return initials;
  }, [formPrefilledAnswers, violationReasonQuestion, clearanceReasonQuestion]);

  const isViolationFound = useMemo(() => {
    if (!violationQuestion) return false;
    const selectedOptionIds = answers[violationQuestion.question_id] || [];
    const selectedOption = violationQuestion.options.find(opt =>
      selectedOptionIds.includes(opt.option_id)
    );
    return selectedOption?.option_tag === OptionTag.VIOLATION_FOUND;
  }, [violationQuestion, answers]);

  const wasInitiallyViolation = useMemo(() => {
    if (!violationQuestion) return false;
    const answer = formPrefilledAnswers.find(
      (a: ApiAuditTrailAnswer) => a.question_id === violationQuestion.question_id
    );
    if (!answer?.selected_option_ids?.length) return false;
    const selectedOption = violationQuestion.options.find(opt =>
      answer.selected_option_ids.includes(opt.option_id)
    );
    return selectedOption?.option_tag === OptionTag.VIOLATION_FOUND;
  }, [violationQuestion, formPrefilledAnswers]);

  const multiSelectQuestion = useMemo(() => {
    if (isViolationFound) return violationReasonQuestion;
    if (auditStatus === AuditStatus.PENDING) return null;
    return clearanceReasonQuestion;
  }, [isViolationFound, violationReasonQuestion, clearanceReasonQuestion, auditStatus]);

  useEffect(() => {
    if (formPrefilledAnswers.length === 0) {
      setAnswers({});
      setErrorRemovalReasons({});
      return;
    }
    const initialAnswers: Record<number, number[]> = {};
    formPrefilledAnswers.forEach((answer: ApiAuditTrailAnswer) => {
      initialAnswers[answer.question_id] = answer.selected_option_ids;
    });
    setAnswers(initialAnswers);
    setErrorRemovalReasons({});
  }, [formPrefilledAnswers]);

  const removedErrors: RemovedError[] = useMemo(() => {
    if (!violationReasonQuestion || !wasInitiallyViolation) return [];

    const questionId = violationReasonQuestion.question_id;
    const initialIds = initialMultiSelectIds[questionId] || [];
    if (!initialIds.length) return [];

    const currentIds = isViolationFound ? answers[questionId] || [] : [];

    return initialIds
      .filter(id => !currentIds.includes(id))
      .map(id => {
        const option = violationReasonQuestion.options.find(opt => opt.option_id === id);
        return option ? { optionId: option.option_id, optionText: option.option_text } : null;
      })
      .filter((err): err is RemovedError => err !== null);
  }, [
    violationReasonQuestion,
    wasInitiallyViolation,
    isViolationFound,
    initialMultiSelectIds,
    answers
  ]);

  const perViolationReasonOptions = useMemo(() => {
    if (!violationReasonQuestion) return {};
    const result: Record<number, IOption[]> = {};
    removedErrors.forEach(err => {
      const violationOption = violationReasonQuestion.options.find(
        opt => opt.option_id === err.optionId
      );
      if (violationOption?.question_id) {
        const subQuestion = auditQuestionnaire.find(
          q => q.question_id === violationOption.question_id
        );
        if (subQuestion) {
          result[err.optionId] = getDropdownOptions(subQuestion.options);
        }
      }
    });
    return result;
  }, [removedErrors, violationReasonQuestion, auditQuestionnaire]);

  const hasUnexplainedRemovals = useMemo(() => {
    return removedErrors.some(err => {
      const reasonOptions = perViolationReasonOptions[err.optionId];
      if (!reasonOptions || reasonOptions.length === 0) return false;
      return !errorRemovalReasons[err.optionId] || errorRemovalReasons[err.optionId].length === 0;
    });
  }, [removedErrors, errorRemovalReasons, perViolationReasonOptions]);

  const isFormIncomplete = useMemo(() => {
    if (!multiSelectQuestion) return false;
    const selectedIds = answers[multiSelectQuestion.question_id] || [];
    return selectedIds.length === 0;
  }, [multiSelectQuestion, answers]);

  const getSelectedOption = (questionId: number): string => {
    const selectedIds = answers[questionId];
    return selectedIds?.length ? String(selectedIds[0]) : '';
  };

  const handleSingleSelectChange = (questionId: number, optionId: number) => {
    setAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: [optionId] };
      if (violationQuestion && questionId === violationQuestion.question_id) {
        if (violationReasonQuestion) delete newAnswers[violationReasonQuestion.question_id];
        if (clearanceReasonQuestion) delete newAnswers[clearanceReasonQuestion.question_id];
      }
      return newAnswers;
    });
  };

  const handleMultiSelectChange = useCallback((questionId: number, options: IOption[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: options.map(opt => parseInt(opt.value, 10))
    }));
  }, []);

  const handleErrorRemovalReasonChange = useCallback(
    (removedErrorOptionId: number, selectedOptions: IOption[]) => {
      setErrorRemovalReasons(prev => ({
        ...prev,
        [removedErrorOptionId]: selectedOptions
      }));
    },
    []
  );

  useEffect(() => {
    if (removedErrors.length === 0) {
      setErrorRemovalReasons({});
      return;
    }
    const currentRemovedIds = new Set(removedErrors.map(e => e.optionId));
    setErrorRemovalReasons(prev => {
      const cleaned: Record<number, IOption[]> = {};
      Object.entries(prev).forEach(([key, val]) => {
        if (currentRemovedIds.has(Number(key))) {
          cleaned[Number(key)] = val;
        }
      });
      return cleaned;
    });
  }, [removedErrors]);

  const getSelectedMultiOptions = (
    questionId: number,
    question: AuditQuestionConfig
  ): IOption[] => {
    const selectedIds = answers[questionId] || [];
    return selectedIds
      .map(id => {
        const option = question.options.find(opt => opt.option_id === id);
        if (!option || option.option_tag === OptionTag.UNDEFINED) return null;
        return { value: String(option.option_id), label: option.option_text };
      })
      .filter((opt): opt is IOption => opt !== null);
  };

  const isSubmitDisabled = useMemo((): boolean => {
    if (auditStatus === AuditStatus.PENDING) {
      return hasUnexplainedRemovals || isFormIncomplete;
    }
    if (auditStatus === AuditStatus.AGENT_CLEARED || auditStatus === AuditStatus.AGENT_VIOLATION) {
      if (isQcAgent) return true;
      if (isTeamLead) return hasUnexplainedRemovals || isFormIncomplete;
    }
    return true;
  }, [auditStatus, isQcAgent, isTeamLead, hasUnexplainedRemovals, isFormIncomplete]);

  const isStatusDisabled = useMemo((): boolean => {
    if (auditStatus === AuditStatus.PENDING) return false;
    if (auditStatus === AuditStatus.AGENT_CLEARED || auditStatus === AuditStatus.AGENT_VIOLATION) {
      if (isQcAgent) return true;
      if (isTeamLead) return false;
    }
    return true;
  }, [auditStatus, isQcAgent, isTeamLead]);

  const isFormDisabled = isStatusDisabled || readOnly;

  const handleSubmit = () => {
    const formattedAnswers: AuditAnswer[] = Object.entries(answers).map(
      ([questionId, selectedOptionIds]) => ({
        questionId: parseInt(questionId, 10),
        selectedOptionIds
      })
    );

    if (violationReasonQuestion) {
      removedErrors.forEach(err => {
        const selectedReasons = errorRemovalReasons[err.optionId];
        if (!selectedReasons?.length) return;
        const violationOption = violationReasonQuestion.options.find(
          opt => opt.option_id === err.optionId
        );
        if (!violationOption?.question_id) return;
        formattedAnswers.push({
          questionId: violationOption.question_id,
          selectedOptionIds: selectedReasons.map(opt => parseInt(opt.value, 10))
        });
      });
    }

    const successCallback = () => onSubmit?.(formattedAnswers);

    const payload = { answers: formattedAnswers };

    if (isQcAgent) {
      dispatch(submitQcAgentAudit(auditAssignmentReferenceId, payload, successCallback));
    } else {
      dispatch(
        submitQcTeamLeadAudit(
          auditAssignmentReferenceId,
          { ...payload, tlReferenceId },
          successCallback
        )
      );
    }
  };

  if (isSidebarLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoaderIcon
          size={40}
          borderWidth={4}
          borderColor="var(--navi-color-gray-border)"
          fillColor="var(--navi-color-blue-base)"
        />
      </div>
    );
  }

  const showMultiSelect = multiSelectQuestion && !isStatusDisabled && !readOnly;
  const showRemovalReasons = !readOnly && !isStatusDisabled && removedErrors.length > 0;

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full bg-[var(--bg-primary)] border-b border-[var(--grayscale-6)] flex-shrink-0">
        <EmbeddedPlayer url={recordingUrl} />
      </div>

      <div className="flex-shrink-0">
        <DetailsPopper isTDME={readOnly} isDrawerOpen={isDrawerOpen} />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {isQcAgent && dsAuditTrail && <PreAuditBanner />}

        <SingleSelectQuestions
          questions={singleSelectQuestions}
          getSelectedOption={getSelectedOption}
          handleSingleSelectChange={handleSingleSelectChange}
          isFormDisabled={isFormDisabled}
          readOnly={readOnly}
        />

        {violationQuestion && (
          <ViolationQuestion
            question={violationQuestion}
            getSelectedOption={getSelectedOption}
            handleSingleSelectChange={handleSingleSelectChange}
            isFormDisabled={isFormDisabled}
            readOnly={readOnly}
          />
        )}

        <PreAuditValuesSection readOnly={readOnly} />

        {showMultiSelect && (
          <MultiSelectErrorSection
            question={multiSelectQuestion}
            selectedOptions={getSelectedMultiOptions(
              multiSelectQuestion.question_id,
              multiSelectQuestion
            )}
            onSelectionChange={options =>
              handleMultiSelectChange(multiSelectQuestion.question_id, options)
            }
          />
        )}

        {showRemovalReasons && (
          <ErrorRemovalReasonSection
            removedErrors={removedErrors}
            perViolationReasonOptions={perViolationReasonOptions}
            errorRemovalReasons={errorRemovalReasons}
            onReasonChange={handleErrorRemovalReasonChange}
          />
        )}
      </div>

      {!readOnly && <Footer handleSubmit={handleSubmit} isSubmitDisabled={isSubmitDisabled} />}
    </div>
  );
};

export default AuditFeedbackForm;
